import {
  CatalogCategoryList_VariantPacksQuery,
  CatalogCategoryList_VariantPacksQueryVariables,
  ItemsContext_VariantsPackFieldsFragment,
} from '@/types';
import { CatalogCategoryType } from '@/contexts/item-context-types';
import { CategoryListReturnType } from '@/utils/category';
import { gql } from '@apollo/client';
import { useAdaptAndGroupVariantPacks } from './use-adapt-and-group-item-list-options';
import { useCallback, useMemo } from 'react';
import { useCatalogFilteringEnabled } from './dynamic-config/use-catalog-filtering-enabled';
import {
  useCatalogVariantPacksQueryVariables,
  VARIANTS_PACK_FRAGMENT,
} from '@/contexts/items-context-queries';
import { useDynamicConfigValue } from './use-dynamic-config-value';
import { useGTINMetadataFilters } from './use-gtin-metadata-filters';
import { useItemQueryVariables } from './use-item-query-variables';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from './use-wrapped-query';

export const CATEGORY_VARIANT_PACKS_QUERY = gql`
  fragment CatalogCategoryList_GTINFiltersResponse on GetGTINFiltersForCategoryResponse {
    flag_filters
    list_filters {
      brand_names
      free_from_allergens
      origin_countries
      pack_sizes
      subcategories
    }
  }

  query CatalogCategoryList_VariantPacks(
    $categoryName: String
    $limit: Int
    $locale: String!
    $offset: Int
    $orderBy: [visible_variant_packs_order_by!]!
    # restaurantUUID is used in the ItemsContext_VariantsPackFields fragment
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $skipCount: Boolean!
    $skipFilters: Boolean!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $where: visible_variant_packs_bool_exp!
  ) {
    visible_variant_packs_aggregate(where: $where) @skip(if: $skipCount) {
      aggregate {
        count
      }
    }

    visible_variant_packs(
      limit: $limit
      offset: $offset
      where: $where
      order_by: $orderBy
    ) {
      variant_pack {
        ...ItemsContext_VariantsPackFields
      }
    }

    getGtinFiltersForCategory(
      category: $categoryName
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) @skip(if: $skipFilters) {
      ...CatalogCategoryList_GTINFiltersResponse
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

export interface CatalogCategoryListProps {
  category?: CatalogCategoryType;
}

const PAGE_LIMIT = 500;

export const useCatalogVariantPacksList = ({
  category,
}: CatalogCategoryListProps): CategoryListReturnType => {
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks(false);
  const itemQueryVariables = useItemQueryVariables();
  const { supplierUUID } = useNewOrderContext();
  const { isGuestMode } = useUserContext();
  const { restaurantUUID, visibleVariantPacksOrderBy } =
    useCatalogVariantPacksQueryVariables(supplierUUID!);

  const catalogFilteringEnabled = useCatalogFilteringEnabled();
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');

  const queryVariables =
    useMemo<CatalogCategoryList_VariantPacksQueryVariables>(() => {
      return {
        ...itemQueryVariables,
        categoryName: category?.name ?? null,
        limit: PAGE_LIMIT,
        orderBy: visibleVariantPacksOrderBy,
        restaurantUUID,
        skipCount: false,
        skipFilters: !catalogFilteringEnabled,
        supplierUUID: supplierUUID!,
        where: {
          category: category?.name
            ? { _eq: category.name }
            : { _is_null: true },
          restaurant_uuid: { _eq: restaurantUUID },
          supplier_uuid: { _eq: supplierUUID },
        },
      };
    }, [
      catalogFilteringEnabled,
      category,
      itemQueryVariables,
      restaurantUUID,
      supplierUUID,
      visibleVariantPacksOrderBy,
    ]);

  const { data, loading, fetchMore } = useWrappedQuery<
    CatalogCategoryList_VariantPacksQuery,
    CatalogCategoryList_VariantPacksQueryVariables
  >(CATEGORY_VARIANT_PACKS_QUERY, {
    context: {
      anonymous: isGuestMode,
    },
    skip: itemGroupsEnabled || !category,
    variables: queryVariables,
  });

  const fetchMoreOffset = useMemo(() => {
    const currentCount = data?.visible_variant_packs.length ?? 0;
    const totalCount =
      data?.visible_variant_packs_aggregate?.aggregate?.count ?? 0;
    return currentCount < totalCount ? currentCount : null;
  }, [data]);

  const visibleVPs = useMemo(() => {
    return (data?.visible_variant_packs || []).reduce<
      ItemsContext_VariantsPackFieldsFragment[]
    >((acc, vvp) => {
      const variantPack = vvp.variant_pack;
      if (variantPack) {
        acc.push(variantPack);
      }
      return acc;
    }, []);
  }, [data?.visible_variant_packs]);

  const { gtinFilters, matchingVariantPackUUIDs } = useGTINMetadataFilters(
    visibleVPs,
    catalogFilteringEnabled ?? false,
    data?.getGtinFiltersForCategory,
  );

  const filtersAvailable = useMemo(() => {
    return (
      gtinFilters.listFilters.some((f) => f.isAvailable) ||
      gtinFilters.flagFilters.some((f) => f.isAvailable)
    );
  }, [gtinFilters]);

  const fetchNextPage = useCallback(async () => {
    if (typeof fetchMoreOffset === 'number') {
      await fetchMore<
        CatalogCategoryList_VariantPacksQuery,
        CatalogCategoryList_VariantPacksQueryVariables
      >({
        updateQuery: (prevResult, { fetchMoreResult }) => {
          return fetchMoreResult
            ? {
                ...prevResult,
                visible_variant_packs: [
                  ...prevResult.visible_variant_packs,
                  ...fetchMoreResult.visible_variant_packs,
                ],
              }
            : prevResult;
        },
        variables: {
          ...queryVariables,
          offset: fetchMoreOffset,
          skipCount: true,
          skipFilters: true,
        },
      });
    }
  }, [fetchMore, fetchMoreOffset, queryVariables]);

  const listData = useMemo(() => {
    return adaptAndGroupVariantPacks(
      catalogFilteringEnabled
        ? visibleVPs.filter((vp) => matchingVariantPackUUIDs.has(vp.uuid))
        : visibleVPs,
    );
  }, [
    adaptAndGroupVariantPacks,
    catalogFilteringEnabled,
    visibleVPs,
    matchingVariantPackUUIDs,
  ]);

  return useMemo(
    () => ({
      fetchMoreOffset,
      fetchNextPage,
      filtersAvailable,
      gtinFilters,
      listData,
      loading,
      subcategoryItemCounts: undefined,
    }),
    [
      fetchMoreOffset,
      fetchNextPage,
      filtersAvailable,
      gtinFilters,
      listData,
      loading,
    ],
  );
};
